import React, { useCallback, useState } from 'react';
import { noop, partial, times } from 'lodash';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import RubricBlock from '@dyl/blocks/RubricBlock';
import { Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AudioAndTranscriptBlock from "@dyl/blocks/AudioAndTranscriptBlock";
import ImagePicker from "@dyl/components/ImagePicker";
import indexToLetter from "@dyl/utils/indexToLetter";
import { useImage } from "@dyl/systems/ImagesManager";
import DragCanvas, { DragCanvasImage } from "@dyl/components/DragCanvas";
import SectionDividers from "@dyl/components/SectionDividers";
import { AnswerDraggableText } from "@dyl/task-types/sharedLogic/SpeakingGenericEditor/PlacementEditor";
import ChipList from "@dyl/components/ChipList";

const CHIPLIST_CHARACTER_LIMIT = 750;
const useStyles = makeStyles(theme => ({
    root: {
        // Preview canvas this ignores this limitation (of 900px)
        maxWidth: theme.breakpoints.values.md,
    },
    pickersContainer: {
        maxWidth: theme.breakpoints.values.md,
    },
    textBox: {},
    groupHeader: {
        marginBottom: '16px',
        textAlign: 'center'
    },
}));



export default function PictureEditingEditor() {
    const classes = useStyles();
    const [items] = useTaskField('items');

    return (
        <div className={classes.root}>
            <SectionDividers>
                {times(items.length, i => <Item key={i} index={i} phase={items[i].phase}/>)}
            </SectionDividers>
        </div>
    );
}

function Item({ index, phase = 'PHASE_1' }) {
    const classes = useStyles();
    const useField = partial(useTaskField, 'items', index);
    const [isExample] = useField('is_example');
    const [audioId, setAudioId, audioError] = useField('audio_id');

    const [items] = useTaskField('items');
    const examplesUntilNow = items
        .slice(0, index)
        .filter(x => x.is_example)
        .reduce(x => x + 1, 0);

    return (
        <>
            { index === 0 &&
                <Typography variant='h4' className={classes.groupHeader}>Part 1</Typography>
            }
            { index === 5 &&
                <Typography variant='h4' className={classes.groupHeader}>Part 2</Typography>
            }

            <Typography variant='h6'>
                { isExample ? `Example ${examplesUntilNow + 1}` : `Item ${index + 1 - examplesUntilNow}`}
            </Typography>

            <Grid container spacing={0}>
                <Grid item xs={12} sm={8}>

                    {(isExample || index === 5) && (
                        <Grid item xs={12}>
                            {/* No phase logic is added for PE*/}

                            <RubricBlock index={index}/>
                        </Grid>
                    )}

                    <AudioAndTranscriptBlock
                        value={audioId ?? ''}
                        onChange={id => setAudioId(id)}
                        label='Stimulus Audio'
                        error={audioError}
                        required
                    />

                </Grid>
                {phase === 'PHASE_1'
                    ? <Phase1PlacementEditor index={index} />
                    : <PhaseTextPlacementEditor index={index} />
                }
            </Grid>
        </>
    );
}

function Phase1PlacementEditor(
    {
        index,
        draggable = true,
        backgroundImagePath = 'background_image',
    }) {
    const [backgroundImageId] = useTaskField(backgroundImagePath);
    const { src: backgroundImageSrc } = useImage(backgroundImageId);

    const [answers] = useTaskField('items', index, 'answers');
    const [shownImage, setShownImage] = useState(0);

    const setShown = useCallback((index) => () => {
        if (index === 0 || answers[index].image_id !== null) { setShownImage(index); }
    }, [answers]);

    return (
        <Grid container spacing={1}>
            <P1ImagePicker
                item md={4}
                onChange={setShown(0)}
                label={'Key'}
                path={['items', index, 'answers', 0, 'image_id']}
            />
            { /* onChange is required as onMouseOut may not be triggered directly after load */ }
            <P1ImagePicker
                item md={4}
                onChange={setShown(0)}
                onMouseOut={setShown(0)}
                onMouseOver={setShown(1)}
                label={'Distractor 1'}
                path={['items', index, 'answers', 1, 'image_id']}
            />
            { /* onChange is required as onMouseOut may not be triggered directly after load */ }
            <P1ImagePicker
                item md={4}
                onChange={setShown(0)}
                onMouseOut={setShown(0)}
                onMouseOver={setShown(2)}
                label={'Distractor 2'}
                path={['items', index, 'answers', 2, 'image_id']}
            />

            <Grid style={{ marginTop: 4, marginBottom: 6, }}/>

            {/* Canvas ignores Grid sizing, this is intentional to preview at the intended size of 1024x768 */}
            <Grid item md={12}>
                <DragCanvas backgroundSrc={backgroundImageSrc}>

                    {/*<PreviousImages index={index} />*/}
                    {times(answers.length, answerIndex => (
                        <AnswerDraggableBoundingBox
                            key={answerIndex}
                            index={answerIndex}
                            itemIndex={index}
                            disabled={!draggable}
                            imagePath={['items', index, 'answers', shownImage, 'image_id']}
                        />
                    ))} {/**/}
                </DragCanvas>
            </Grid>
        </Grid>
    );
}

function P1ImagePicker({ label, path, onChange = noop, ...props }) {
    const [imageId, _setImageId, imageError] = useTaskField(...path);
    const setImageId = useCallback((...args) => {
        _setImageId(...args);
        onChange(...args);
    }, []);
    return (
        <Grid {...props}>
            <ImagePicker
                fullWidth
                label={label}
                value={imageId ?? ''}
                onChange={setImageId}
                error={!!imageError}
            />
        </Grid>
    );
}

function AnswerDraggableBoundingBox({ index, itemIndex, disabled = false, imagePath = null }) {
    imagePath = imagePath || ['items', itemIndex, 'answers', index, 'image_id'];
    const [imageId] = useTaskField(...imagePath);
    const { src } = useImage(imageId);

    const useField = partial(useTaskField, 'items', itemIndex, 'answers', index);
    const [isCorrectAnswer] = useField('is_correct_answer');
    const [coordinates, setCoordinates] = useField('coordinates');

    return (
        <DragCanvasImage
            text={indexToLetter(index)}
            disabled={disabled}
            src={src}
            alt={imageId}
            coordinates={coordinates}
            onCoordinatesChange={setCoordinates}
            imageStyle = {{
                opacity: 0.45,
                outlineColor: isCorrectAnswer ? 'green' : 'red',
                outlineStyle: 'dashed',
                outlineWidth: '4px'
            }}
        />
    );
}

function PhaseTextPlacementEditor(
    {
        index,
        draggable = true,
        backgroundImagePath = 'background_image',
    }) {
    const [backgroundImageId] = useTaskField(backgroundImagePath);
    const { src: backgroundImageSrc } = useImage(backgroundImageId);

    const [answers] = useTaskField('items', index, 'answers');
    const [keys, setKeys, keysError] = useTaskField('items', index, 'keys');

    console.log(keys);
    return (
        <Grid container spacing={0}>

            <Grid item xs={12} sm={6}>
                <ChipList
                    label='Correct Answer(s)'
                    helperText={keysError || index === 0
                        ? 'The correct example answer which will be typed'
                        : 'The allowed correct answer(s) which the user should type one of.'}
                    error={!!keysError}
                    value={keys}
                    onChange={setKeys}
                    // Example only allows one.
                    {...(index === 0 && { max: 1 })}
                    maxLen = {CHIPLIST_CHARACTER_LIMIT}
                    isPictureEditing = {true}
                />
            </Grid>
            <Grid item md={12} style={{ marginTop: 4, marginBottom: 6, }}/>
            {/* Canvas ignores Grid sizing, this is intentional to preview at the intended size of 1024x768 */}
            <Grid item md={12}>
                <DragCanvas backgroundSrc={backgroundImageSrc}>
                    {times(answers.length, answerIndex => (
                        <AnswerDraggableText
                            key={answerIndex}
                            index={answerIndex}
                            itemIndex={index}
                            disabled={!draggable}
                            textPath={['items', index, 'keys', 0]}
                        />
                    ))}
                </DragCanvas>
            </Grid>
        </Grid>
    );
}